import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Plus, Funnel, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGES = ["Inquiry", "Application", "Documents", "Offer", "Enrolled"];

export default function Leads() {
  const { t } = useTranslation();
  const [leads, setLeads] = useState([]);
  const [show, setShow] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", program: "B.Tech CSE", source: "Website" });

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    try {
      const { data } = await api.post("/leads", { ...form, stage: "Inquiry" });
      if (data?.duplicate) {
        toast.warning(`Duplicate detected — existing lead: ${data.lead.name} (score ${data.lead.score})`);
      } else {
        toast.success(`Lead added — AI score ${data.score}`);
      }
      setShow(false);
      setForm({ name: "", email: "", phone: "", program: "B.Tech CSE", source: "Website" });
      load();
    } catch { toast.error("Failed to add lead"); }
  };

  const openTimeline = async (lead) => {
    try {
      const { data } = await api.get(`/leads/${lead.lead_id}/timeline`);
      setTimeline({ lead, events: data });
    } catch { toast.error("Failed to load timeline"); }
  };

  const grouped = STAGES.reduce((acc, s) => ({ ...acc, [s]: leads.filter(l => l.stage === s) }), {});

  return (
    <div>
      <PageHeader
        overline="M01 · Pre-Enrollment"
        title={t("modules.leads.title")}
        description={t("modules.leads.subtitle")}
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-md" data-testid="filter-leads-btn"><Funnel size={14} className="mr-1.5"/> Filter</Button>
            <Button onClick={() => setShow(true)} className="rounded-md bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="add-lead-btn">
              <Plus size={14} className="mr-1.5"/> Add Lead
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* Funnel stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border rounded-md overflow-hidden">
          {STAGES.map((s, i) => (
            <div key={s} className="bg-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-mono-eos text-muted-foreground">0{i+1}</span>
                <div className="eos-overline">{s}</div>
              </div>
              <div className="font-heading font-extrabold text-3xl tracking-tight">{grouped[s]?.length || 0}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{i === 0 ? "New this week" : `Conv. ${Math.max(0, 80 - i*12)}%`}</div>
            </div>
          ))}
        </div>

        {/* Kanban */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {STAGES.map(stage => (
            <div key={stage} className="bg-secondary/50 border border-border rounded-md p-3 min-h-[300px]">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold tracking-tight">{stage}</div>
                <div className="text-[10px] text-muted-foreground">{grouped[stage]?.length || 0}</div>
              </div>
              <div className="space-y-2">
                {grouped[stage]?.map(l => (
                  <button key={l.lead_id} onClick={() => openTimeline(l)} className="w-full text-left bg-white border border-border rounded-md p-3 hover:border-foreground/40" data-testid={`lead-card-${l.lead_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="font-medium text-[13px] truncate">{l.name}</div>
                      <div className={`text-[10px] px-1.5 py-0.5 rounded ${l.score >= 80 ? "bg-emerald-50 text-emerald-700" : l.score >= 65 ? "bg-amber-50 text-amber-700" : "bg-secondary text-muted-foreground"}`}>
                        {l.score}
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{l.email}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="text-[11px] text-foreground/80">{l.program}</div>
                      <div className="text-[10px] text-muted-foreground">{l.source}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* AI score panel */}
        <div className="eos-card flex items-center gap-4">
          <Sparkle size={20} weight="fill" className="text-[hsl(20_90%_48%)]"/>
          <div className="flex-1">
            <div className="text-sm font-medium">AI lead score recalculated — 4 new high-intent leads moved to "Documents".</div>
            <div className="text-xs text-muted-foreground mt-0.5">Score factors: web sessions, source quality, response latency, document completeness.</div>
          </div>
          <Button variant="outline" className="rounded-md" data-testid="ai-rerun-btn">Re-run <ArrowRight size={12} className="ml-1"/></Button>
        </div>
      </div>

      {/* Modal */}
      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-lead-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShow(false)}/>
          <div className="relative bg-white rounded-md border border-border w-full max-w-md p-6">
            <h3 className="font-heading font-bold text-xl tracking-tight">Add new lead</h3>
            <p className="text-sm text-muted-foreground mt-1">They'll enter the funnel at stage <b>Inquiry</b>.</p>
            <div className="mt-5 space-y-3">
              {[
                ["name", "Full name"],
                ["email", "Email"],
                ["phone", "Phone"],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="eos-overline">{label}</label>
                  <input
                    value={form[k]}
                    onChange={e => setForm({ ...form, [k]: e.target.value })}
                    className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:border-foreground/40"
                    data-testid={`lead-input-${k}`}
                  />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eos-overline">Program</label>
                  <select value={form.program} onChange={e => setForm({...form, program: e.target.value})} className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-white">
                    {["B.Tech CSE", "B.Tech ECE", "MBA Core", "M.Tech AI", "B.Sc Physics"].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="eos-overline">Source</label>
                  <select value={form.source} onChange={e => setForm({...form, source: e.target.value})} className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-white">
                    {["Website", "Google Ads", "Instagram Ads", "Referral", "Education Fair"].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShow(false)} className="rounded-md" data-testid="cancel-lead-btn">Cancel</Button>
              <Button onClick={submit} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="submit-lead-btn">Add lead</Button>
            </div>
          </div>
        </div>
      )}
      {/* Lead Timeline Drawer */}
      {timeline && (
        <div className="fixed inset-0 z-50 flex" data-testid="lead-timeline-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setTimeline(null)}/>
          <div className="w-full max-w-md bg-white shadow-xl border-l border-border overflow-y-auto">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="eos-overline">Lead timeline</div>
                <div className="font-heading font-bold text-lg mt-0.5">{timeline.lead.name}</div>
                <div className="text-[11px] text-muted-foreground">{timeline.lead.email} · score {timeline.lead.score}</div>
              </div>
              <button onClick={() => setTimeline(null)} className="text-muted-foreground hover:text-foreground" data-testid="close-timeline-btn">✕</button>
            </div>
            <div className="p-5 space-y-3">
              {timeline.events.length === 0 && <div className="text-sm text-muted-foreground italic">No events yet.</div>}
              {timeline.events.map(ev => (
                <div key={ev.event_id} className="border-l-2 border-[hsl(244_53%_20%)] pl-3 py-1" data-testid={`timeline-event-${ev.event_id}`}>
                  <div className="text-[11px] text-muted-foreground">{new Date(ev.at).toLocaleString()} · {ev.actor}</div>
                  <div className="text-sm font-medium mt-0.5">{ev.type.replace(/_/g, " ")}</div>
                  {ev.data && Object.keys(ev.data).length > 0 && (
                    <pre className="text-[10px] text-muted-foreground mt-1 bg-secondary/50 p-2 rounded overflow-x-auto">{JSON.stringify(ev.data, null, 2)}</pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
